import { types, flow, applySnapshot } from 'mobx-state-tree';

import api from 'services/API';
import { BusinessHour } from 'models/types/businessHour';
import { daysOfWeekFormsByFullForm, daysOrderByForm, daysByOrder } from 'config/calendar';
import dateUtilities from 'utils/dateUtilities';

export const businessHoursInitialState = {
  all: [],
};

export const businessHoursModel = types
  .model({
    all: types.array(BusinessHour),
  })
  .views(self => ({
    formatBusinessHourToDisplay(businessHour) {
      try {
        return {
          id: businessHour.id,
          day: businessHour.day_of_week,
          shortcut: daysOfWeekFormsByFullForm.shortForm[businessHour.day_of_week],
          from: dateUtilities.formatTimeTo12Hour(businessHour.open_time),
          to: dateUtilities.formatTimeTo12Hour(businessHour.close_time),
          isClosed: businessHour.is_closed,
        };
      } catch (err) {
        console.error(err);
        return {};
      }
    },

    get businessHours() {
      try {
        return self.all
          .map(businessHour => {
            try {
              return self.formatBusinessHourToDisplay(businessHour);
            } catch (err) {
              console.error(err);
              return {};
            }
          })
          .sort((a, b) => {
            return daysOrderByForm.fullForm[a.day] - daysOrderByForm.fullForm[b.day];
          });
      } catch (err) {
        return [];
      }
    },
  }))
  .actions(self => ({
    setBusinessHours(businessHours) {
      self.all.replace(businessHours);
    },

    patchBusinessHour: flow(function* (id, body) {
      try {
        validatePayload();
        const res = yield api.patchBusinessHour(body, { id });
        const updatedBusinessHour = res.data.row;
        const existingItem = self.all.find(businessHour => businessHour.id === id);
        if (existingItem) applySnapshot(existingItem, updatedBusinessHour);
        return self.formatBusinessHourToDisplay(updatedBusinessHour);
      } catch (err) {
        return Promise.reject(err);
      }

      function validatePayload() {
        if ((body.open_time && !body.close_time) || (body.close_time && !body.open_time)) {
          throw new Error(
            'Both open_time and close_time must be provided while updating business_hours',
          );
        }
      }
    }),

    patchEstablishmentDayWeekStarts: flow(function* (dayNumber) {
      try {
        const day = daysByOrder[dayNumber];
        if (!day) return [];

        const businessHoursRes = yield api.getBusinessHours();
        const businessHours = businessHoursRes.data.result;
        if (!businessHours.length) return [];

        // Reset is_day_week_begins for all the establishment business hours.
        // Needed for further update request to succeed as is_day_week_begins is a unique constraint
        const resetWeekStartBusinessHourPayload = businessHours.map(businessHour => ({
          id: businessHour.id,
          is_day_week_begins: false,
        }));
        yield api.patchBusinessHours(resetWeekStartBusinessHourPayload);

        const weekStartBusinessHourIdx = businessHours.findIndex(
          businessHour => businessHour.day_of_week === day,
        );
        if (weekStartBusinessHourIdx === -1) return [];
        const weekStartBusinessHourId = businessHours[weekStartBusinessHourIdx].id;
        const updateWeekStartBusinessHourPayload = {
          is_day_week_begins: true,
        };
        yield api.patchBusinessHour(updateWeekStartBusinessHourPayload, {
          id: weekStartBusinessHourId,
        });

        // Fetch fresh data
        const updatedBusinessHoursRes = yield api.getBusinessHours();
        const updatedBusinessHours = updatedBusinessHoursRes.data.result;
        self.setBusinessHours(updatedBusinessHours);

        return updatedBusinessHours.map(businessHour =>
          self.formatBusinessHourToDisplay(businessHour),
        );
      } catch (err) {
        return Promise.reject(err);
      }
    }),
  }));
